/**
 * 검증 issue 를 폼의 자리에 나눈다.
 *
 * issue 의 `path` 는 검증에 보낸 매핑의 경로다. 그래서 나눌 때도 **보낸 매핑**으로 푼다. 편집본이 그 뒤에
 * 바뀌었으면 번호가 다른 칸을 가리킬 수 있다. 그때 화면은 칸에 붙이지 않고 목록으로만 보인다.
 *
 * 자리는 다섯 가지다.
 * - 손 구성(`hands`), 역할 전체(`hands.{i}.{role}`), 역할의 칸(`hands.{i}.{role}.{field}[.…]`).
 * - 카메라 전체(`cameras`), 카메라 줄(`cameras.{i}.…` 는 번호, `cameras.{slot}` 은 slot 이름 → 카메라 번호).
 * - 그 밖(`source` · `time` · `sample` · `evaluation` · 모르는 경로)은 매핑 전체 목록이다.
 *
 * 고칠 자리(`issueTarget`)는 따로 정한다. 확인 필요 항목과 issue 목록의 `수정` 이 같이 쓴다. 폼이 고치지 않는 칸
 * (역할 칸 아래의 `basis` · 근거(`evidence`), action 의 `motion`, 모델 · step rate 같은 최상위 칸)은 JSON 방식이다.
 *
 * Owns:
 * - 경로 → 자리, 자리 키, 경로 → 고칠 자리.
 *
 * Does not own:
 * - 문장 — `mappingIssueMessage.ts`. 경로 이름 — `mappingPath.ts`.
 */
import type { HandRoleName, Mapping, MappingIssue } from "../domain/mapping";

/** 역할 폼의 칸. BE `HandRole` 의 필드 이름 그대로다. */
export const ROLE_FIELDS = [
  "position",
  "orientation",
  "gripper",
  "reference_frame",
  "target_frame",
  "reference_transform",
  "pose_kind",
  "tcp",
  "rotation",
  "reinterpret_reason",
] as const;
export type RoleField = (typeof ROLE_FIELDS)[number];

export type IssueIndex = {
  readonly general: readonly MappingIssue[];
  readonly hands: readonly MappingIssue[];
  readonly cameras: readonly MappingIssue[];
  /** 카메라 번호 → issue. 원본을 고르지 않은 카메라도 있어 원본 이름으로 가리키지 않는다. */
  readonly byCamera: ReadonlyMap<number, readonly MappingIssue[]>;
  /** `roleKey` → 역할 전체 issue. */
  readonly byRole: ReadonlyMap<string, readonly MappingIssue[]>;
  /** `fieldKey` → 칸 issue. */
  readonly byField: ReadonlyMap<string, readonly MappingIssue[]>;
};

export const EMPTY_ISSUE_INDEX: IssueIndex = {
  general: [],
  hands: [],
  cameras: [],
  byCamera: new Map(),
  byRole: new Map(),
  byField: new Map(),
};

export function roleKey(handIndex: number, role: HandRoleName): string {
  return `${handIndex}:${role}`;
}

export function fieldKey(handIndex: number, role: HandRoleName, field: RoleField): string {
  return `${handIndex}:${role}:${field}`;
}

/** issue 를 고칠 자리. 폼의 칸이거나, 폼에 없는 칸이면 JSON 방식이다. */
export type IssueTarget =
  | {
      readonly kind: "form";
      readonly field: RoleField | "hands" | "cameras" | "time";
      readonly focus?: { readonly handIndex: number; readonly role: HandRoleName };
    }
  | { readonly kind: "json" };

function isRoleName(value: string | undefined): value is HandRoleName {
  return value === "observation" || value === "action";
}

function isRoleField(value: string | undefined): value is RoleField {
  return (ROLE_FIELDS as readonly (string | undefined)[]).includes(value);
}

function push<K>(map: Map<K, MappingIssue[]>, key: K, issue: MappingIssue): void {
  const list = map.get(key);
  if (list === undefined) {
    map.set(key, [issue]);
  } else {
    list.push(issue);
  }
}

/**
 * issue 경로를 고칠 자리로 바꾼다. `mapping` 은 issue 를 만든 매핑이다. 고칠 자리가 없으면(매핑 밖 경로 · 없는
 * 손) `null` 이다.
 *
 * @example `hands.0.observation.position.unit` → 폼 `position`(0번 손 관측). `….position.basis` → JSON.
 */
export function issueTarget(path: string, mapping: Mapping): IssueTarget | null {
  const [head, second, role, field, leaf] = path.split(".");
  if (head === "hands") {
    if (second === undefined) return { kind: "form", field: "hands" };
    const handIndex = Number(second);
    if (
      !Number.isInteger(handIndex) ||
      mapping.hands[handIndex] === undefined ||
      !isRoleName(role)
    ) {
      return null;
    }
    const focus = { handIndex, role };
    if (field === undefined) return { kind: "form", field: "hands", focus };
    // 역할 칸 아래의 basis · 근거와 action 의 motion 은 폼에 없다. 근거 상세는 읽기 전용이다.
    const editable =
      isRoleField(field) && leaf !== "basis" && !path.split(".").includes("evidence");
    return editable ? { kind: "form", field, focus } : { kind: "json" };
  }
  if (head === "cameras") return { kind: "form", field: "cameras" };
  if (head === "time_shift_seconds") return { kind: "form", field: "time" };
  return head !== undefined && Object.prototype.hasOwnProperty.call(mapping, head)
    ? { kind: "json" }
    : null;
}

/** issue 를 자리마다 나눈다. `mapping` 은 검증에 보낸 매핑이다. */
export function indexIssues(issues: readonly MappingIssue[], mapping: Mapping): IssueIndex {
  const general: MappingIssue[] = [];
  const hands: MappingIssue[] = [];
  const cameras: MappingIssue[] = [];
  const byCamera = new Map<number, MappingIssue[]>();
  const byRole = new Map<string, MappingIssue[]>();
  const byField = new Map<string, MappingIssue[]>();
  for (const issue of issues) {
    const [head, second, role, field] = issue.path.split(".");
    if (head === "hands") {
      const handIndex = Number(second);
      if (second === undefined) {
        hands.push(issue);
      } else if (
        !Number.isInteger(handIndex) ||
        mapping.hands[handIndex] === undefined ||
        !isRoleName(role)
      ) {
        general.push(issue);
      } else if (field === undefined) {
        push(byRole, roleKey(handIndex, role), issue);
      } else if (isRoleField(field)) {
        push(byField, fieldKey(handIndex, role, field), issue);
      } else {
        push(byRole, roleKey(handIndex, role), issue);
      }
      continue;
    }
    if (head === "cameras") {
      if (second === undefined) {
        cameras.push(issue);
        continue;
      }
      const numbered = /^\d+$/.test(second) && mapping.cameras[Number(second)] !== undefined;
      const index = numbered
        ? Number(second)
        : mapping.cameras.findIndex((item) => item.slot === second);
      if (index < 0) {
        cameras.push(issue);
      } else {
        push(byCamera, index, issue);
      }
      continue;
    }
    general.push(issue);
  }
  return { general, hands, cameras, byCamera, byRole, byField };
}
